import { useState } from 'react';
import supabase from '../../lib/supabaseClient';

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SignUpModal({ isOpen, onClose }: SignUpModalProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    onClose();
    setDone(false);
    setError('');
    setEmail('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error: sbError } = await supabase
      .from('subscribers')
      .insert({ email: email.trim(), source: 'signup_modal' });

    setLoading(false);

    if (sbError) {
      if (sbError.code === '23505') {
        // Duplicate — treat as success (already subscribed)
        setDone(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } else {
      setDone(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-gray-600 text-base"></i>
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
              <i className="ri-check-line text-emerald-600 text-2xl"></i>
            </div>
            <h3 className="text-lg font-black text-gray-900 mb-2">You're in!</h3>
            <p className="text-sm text-gray-500">
              Welcome to 247compare. We'll send exclusive deals and updates straight to your inbox!
            </p>
            <button
              onClick={handleClose}
              className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              Awesome, Close
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                <i className="ri-gift-2-line text-white text-base"></i>
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-1">Claim Your Perks</h3>
              <p className="text-xs text-gray-500">Join 50K+ users — get exclusive deals for your projects</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors"
                />
              </div>

              {error && (
                <p className="text-xs text-red-500 font-medium">{error}</p>
              )}

              <div className="space-y-1.5 py-1">
                {[
                  { icon: 'ri-coupon-3-line', text: 'Special discount codes' },
                  { icon: 'ri-vip-crown-line', text: 'Early access to new features' },
                  { icon: 'ri-star-line', text: 'Exclusive perks on 500+ platforms' },
                ].map((perk, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${perk.icon} text-violet-500 text-sm`}></i>
                    </div>
                    <span className="text-xs text-gray-600">{perk.text}</span>
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2 disabled:opacity-60"
              >
                {loading ? 'Signing up...' : 'Claim Your Perks Now →'}
              </button>
            </form>

            <p className="text-xs text-center text-gray-400 mt-4">
              No spam, unsubscribe anytime. We respect your privacy.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
